import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import Artwork from '../components/Artwork.jsx'
import ScorePill from '../components/ScorePill.jsx'
import ErrorState, { LoadingState } from '../components/ErrorState.jsx'
import { ApiError, api } from '../lib/api.js'
import { display } from '../lib/score.js'
import { plural } from '../lib/format.js'

/**
 * `/charts` — community charts (CHART-001, PLAN §16; grown by 1B-3).
 *
 * **Every number that shaped the ordering is printed on the page**, and read
 * from the snapshot rather than hardcoded here: the window, the threshold
 * line (which knows whether the chart is a band, a floor, or a shrunk
 * ranking), `algo_version`, and when it ran. A chart whose thresholds lived
 * in the client would print the current ones beside numbers computed under
 * the old ones the first time either moved.
 *
 * The suppressed count is shown rather than swallowed. "Nothing else
 * qualified" and "we hid 31 records from you" are the same fact, and only one
 * of them is honest — a chart that quietly dropped rows would read as a
 * complete ranking of everything on the site.
 *
 * The 1B-3 sections keep the same discipline with different arithmetic:
 * hidden gems are the 5-24 band ranked by raw mean, divisive is ranked by
 * score spread, and "Among people you follow" is computed per viewer on
 * every read — it is the one chart that is different for everybody, which
 * is why it is not a snapshot and never ranks the people themselves.
 */

// What the pill's number *is*, per formula — printing "adjusted" over a raw
// mean would claim arithmetic that never ran.
const SCORE_LABELS = {
  'shrunk mean': 'Adjusted score',
  'raw mean': 'Average score',
  'standard deviation': 'Score spread',
}

function ChartRow({ entry, scoreLabel }) {
  const media = entry.media
  return (
    <li className="chart-row">
      <span className="chart-rank" aria-label={`Rank ${entry.rank}`}>
        {entry.rank}
      </span>
      {media ? (
        <Link
          to={`/${media.kind === 'song' ? 'song' : 'album'}/${media.public_id}`}
          className="chart-media"
        >
          <Artwork
            art={media.artwork_url ? { url: media.artwork_url } : null}
            title={media.title}
            artist={media.credit_name}
            size={48}
          />
          <span className="row-main">
            <span className="chart-title">{media.title}</span>
            <span className="footnote">
              {media.credit_name}
              {media.year ? ` · ${media.year}` : ''}
            </span>
          </span>
        </Link>
      ) : (
        <span className="row-main footnote">No longer in the catalog</span>
      )}
      <span className="chart-scores">
        <ScorePill score={entry.score} label={`${scoreLabel} ${display(entry.score)}`} />
        <span className="footnote">
          {display(entry.raw_mean)} raw · {entry.qualifier_text}
        </span>
      </span>
    </li>
  )
}

function Chart({ chart }) {
  const scoreLabel = SCORE_LABELS[chart.formula] || 'Score'
  return (
    <section className="card chart fade-up">
      <header className="card-pad chart-head">
        <div className="row-main">
          <h2 className="title-3 mb-0">{chart.title}</h2>
          <p className="footnote mb-0">{chart.subtitle}</p>
        </div>
      </header>

      {chart.entries.length ? (
        <ol className="chart-list">
          {chart.entries.map((entry) => (
            <ChartRow
              key={`${entry.rank}-${entry.media?.public_id || entry.rank}`}
              entry={entry}
              scoreLabel={scoreLabel}
            />
          ))}
        </ol>
      ) : (
        <div className="empty-state">
          <h2>Nothing qualifies yet</h2>
          <p>
            A record needs {chart.min_ratings} {plural(chart.min_ratings, 'rating')} before it can
            be ranked. In a closed alpha
            that takes a while, and ranking things on three opinions would be worse than waiting.
          </p>
        </div>
      )}

      <footer className="chart-foot card-pad">
        <p className="footnote mb-0">
          {chart.window === 'all_time' ? 'All time' : chart.window} · {chart.threshold_text} ·{' '}
          <span className="mono">{chart.algo_version}</span>
        </p>
        {chart.suppressed > 0 ? (
          <p className="footnote mb-0">
            {chart.suppressed} {chart.suppressed === 1 ? 'record has' : 'records have'} ratings but
            not enough to be ranked, and {chart.suppressed === 1 ? 'is' : 'are'} not shown.
          </p>
        ) : null}
        <p className="footnote mb-0">
          {chart.computed_at
            ? `Computed ${new Date(chart.computed_at).toLocaleString()}`
            : 'Not computed yet — the nightly job has not run.'}
        </p>
        {chart.formula === 'shrunk mean' ? (
          <p className="footnote mb-0">
            Community ratings, adjusted towards the site average so a record with few ratings does
            not outrank one with many. These are not official charts.
          </p>
        ) : (
          <p className="footnote mb-0">Community data. These are not official charts.</p>
        )}
      </footer>
    </section>
  )
}

function FriendsChart({ friends }) {
  if (!friends) return null
  return (
    <section className="card chart fade-up">
      <header className="card-pad chart-head">
        <div className="row-main">
          <h2 className="title-3 mb-0">Among people you follow</h2>
        </div>
      </header>

      {friends.computed ? (
        <ol className="chart-list">
          {friends.entries.map((entry) => (
            <ChartRow
              key={`${entry.rank}-${entry.media?.public_id || entry.rank}`}
              entry={entry}
              scoreLabel="Average score"
            />
          ))}
        </ol>
      ) : (
        <div className="empty-state">
          <h2>Follow a few more people who rate albums to see this chart</h2>
          <p>
            It appears once {friends.min_followees} {plural(friends.min_followees, 'person', 'people')}{' '}
            you follow have rated something — right
            now that is {friends.followee_count}.
          </p>
        </div>
      )}

      {friends.computed ? (
        <footer className="chart-foot card-pad">
          <p className="footnote mb-0">
            All time · public ratings from {friends.followee_count}{' '}
            {plural(friends.followee_count, 'person', 'people')} you follow · only you
            see this chart
          </p>
        </footer>
      ) : null}
    </section>
  )
}

export default function Charts() {
  const [data, setData] = useState(null)
  const [error, setError] = useState('')

  const load = useCallback((signal) => {
    setError('')
    return api
      .charts({ signal })
      .then(setData)
      .catch((err) => {
        if (err.name === 'AbortError') return
        setError(err instanceof ApiError ? err.message : 'The charts did not load.')
      })
  }, [])

  useEffect(() => {
    document.title = 'Charts — Sonologue'
    const controller = new AbortController()
    load(controller.signal)
    return () => controller.abort()
  }, [load])

  if (error) {
    return (
      <ErrorState
        title="The charts did not load"
        message={error}
        onRetry={() => load()}
        level={1}
      />
    )
  }

  if (!data) return <LoadingState label="Loading charts" />

  return (
    <>
      <div className="section-header">
        <h1 className="title-2">Charts</h1>
      </div>
      <div className="chart-stack">
        {data.charts.map((chart) => (
          <Chart key={chart.key} chart={chart} />
        ))}
        <FriendsChart friends={data.friends} />
      </div>
    </>
  )
}
